<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
    <div
        class="con-wrapper"
        data-testid="input-consent-item"
    >
        <div
            class="con-container"
            :class="{ disabled, error: isError }"
        >
            <InputCheckbox
                v-model="checked"
                :initial-value="checked"
                :disabled="disabled"
                :error="isError"
            />
            <div ref="el">
                <div
                    v-if="!!consent.longDesc && !!consent.shortDesc"
                    @click="handleClick"
                >
                    <TextParagraph
                        v-if="!expanded"
                        small
                        class="desc text text-dense"
                        v-html="sanitize(consent.shortDesc)"
                    />
                    <TextParagraph
                        v-else
                        small
                        class="text text-dense"
                        v-html="sanitize(consent.longDesc)"
                    />
                </div>
                <div
                    v-else
                    @click="checked = !checked"
                >
                    <TextParagraph
                        small
                        class="text text-dense"
                        v-html="sanitize(consent.shortDesc)"
                    />
                </div>
                <div
                    class="con-expand"
                    data-testid="input-consent-expand"
                    :aria-expanded="expanded"
                    @click="toggleExpand"
                >
                    <div style="height: 8px"></div>
                    <button
                        class="con-expand-btn"
                        style="opacity: 0.5"
                    >
                        {{ !expanded ? t('expand') : t('hide') }}
                        <ArrowRight
                            v-if="!expanded"
                            size="6"
                            style="transform: rotate(90deg); margin-left: 8px; opacity: 0.7"
                        />
                        <ArrowRight
                            v-else
                            size="6"
                            style="transform: rotate(-90deg); margin-left: 8px; opacity: 0.7"
                        />
                    </button>
                </div>
            </div>
        </div>
        <div
            v-if="error"
            style="padding-left: 42px; margin-top: 3px"
        >
            <ErrorText
                :error="error"
                class="con-error"
                clip
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, nextTick } from 'vue'
import marked from 'widget-components/utilities/marked'
import sanitize from 'widget-components/utilities/sanitize'
import InputCheckbox from 'widget-components/components/InputCheckbox'
import TextParagraph from 'widget-components/components/TextParagraph'
import ErrorText from 'widget-components/components/ErrorText'
import ArrowRight from 'widget-components/assets/svg/ArrowRight.vue'
import { useI18n } from '../../utilities/i18n'
import type { Consent } from 'widget-components/types/consent'

const props = defineProps<{
    consent: Consent
    modelValue?: boolean
    disabled?: boolean
    error?: string
    selected?: boolean
}>()

const emit = defineEmits(['update:modelValue', 'ui:toggle-expand'])

const checked = ref(props.selected)
const model = computed(() => props.modelValue)

const isError = computed(() => !!props.error)

const el = ref<HTMLElement | null>(null)

const expanded = ref(false)

const { t } = useI18n()

watch(model, (val) => {
    if (checked.value !== val) checked.value = val
})

watch(checked, (val) => {
    emit('update:modelValue', val)
})

const consent = computed(() => {
    const out = {} as Consent
    if (props.consent.longDesc) out.longDesc = marked(props.consent.longDesc)
    if (props.consent.shortDesc) out.shortDesc = marked(props.consent.shortDesc)
    return out
})

const handleClick = () => {
    if (props.disabled) {
        return
    }
    checked.value = !checked.value
}

const toggleExpand = () => {
    expanded.value = !expanded.value
    emit('ui:toggle-expand', { expanded: expanded.value, label: consent.value.shortDesc })
    nextTick(addListenersToInnerLinks)
}

const addListenersToInnerLinks = () => {
    el.value?.querySelectorAll('a').forEach((a) => {
        a.addEventListener('click', (e) => {
            e.stopPropagation()
        })
    })
}

onMounted(() => {
    try {
        addListenersToInnerLinks()
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
    }
})
</script>

<style lang="sass" scoped>
@import "./InputConsent.sass"
</style>
